<template>
  <div class="main-area">
    <pagination
      :current-page="state.pager.currentPage"
      :total-items="displayData.length"
      :items-per-page="state.pager.itemsPerPage"
      @page-click="onPageClick"
    ></pagination>
    <table class="table admin-table" v-if="displayData.length > 0">
      <thead>
        <tr class="table-secondary header-row">
          <th></th>
          <th
            v-for="field in fields"
            :key="field.name"
          >
            {{ field.label }}
          </th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="e in paginatedData"
          :key="e.id"
          class="data-row"
        >
          <td class="vm head-edit">
            <button
              v-if="shouldShowEditButton(e)"
              class="btn btn-secondary btn-sm"
              :disabled="!canEdit(e)"
              @click="edit(e)"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>
          </td>

          <td
            v-for="field in fields"
            :key="field.name"
            class="vm"
            :class="field.class"
          >
            <template v-if="field.type === 'icon'">
              <img class="icon" :src="e[field.name]">
            </template>

            <template v-else-if="field.type === 'html'">
              <div v-html="fieldValue(e, field.name)"></div>
            </template>

            <template v-else>
              {{ fieldValue(e, field.name) }}
            </template>
          </td>

          <td class="vm ws-nowrap">
            <button
              v-if="shouldShowEditButton(e)"
              class="btn btn-secondary btn-sm"
              :disabled="!canEdit(e)"
              @click="edit(e)"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>

            <button
              v-for="button in visibleCustomButtons(e)"
              :key="button.label"
              class="btn btn-secondary ml8"
              :disabled="button.isDisabled(e)"
              @click="button.clickHandler(e)"
            >
              {{ button.label }}
            </button>

            <button
              v-if="shouldShowDeleteButton(e)"
              class="delete-btn btn btn-danger btn-sm"
              :disabled="!canDelete(e)"
              @click="destroy(e)"
            >
              <i class="fas fa-trash-alt mr4"></i>削除
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <pagination
      :current-page="state.pager.currentPage"
      :total-items="displayData.length"
      :items-per-page="state.pager.itemsPerPage"
      @page-click="onPageClick"
    ></pagination>
  </div>
</template>

<script setup lang="ts" generic="Row extends AdminListTableRow">
import { computed, reactive } from 'vue';
import { paginateItems } from 'src/lib/utils';
import type { PageItem } from 'src/models';
import type { AdminListTableField, AdminListTableRow, CustomButton } from 'src/models/adminListTable';

interface AdminListTableState {
  pager: {
    currentPage: number;
    itemsPerPage: number;
  };
}

interface Props {
  displayData?: Row[];
  fields?: AdminListTableField<Row>[];
  customButtons?: CustomButton<Row>[];
  // メソッド構文（bivariant）で宣言し、呼び出し元が各ドメイン型の具体型で定義できるようにする
  // optional(?)にしないとwithDefaultsのデフォルトがあってもrequired: trueにコンパイルされ、未指定の呼び出し元でVue警告が出る
  canDelete?(e: Row): boolean;
  canEdit?(e: Row): boolean;
  alwaysShowButtons?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  displayData: () => [],
  fields: () => [],
  customButtons: () => [],
  canDelete: () => true,
  canEdit: () => true,
  alwaysShowButtons: false,
});

const emit = defineEmits(['start-edit', 'start-destroy']);

const ITEMS_PER_PAGE = 50;

const state = reactive<AdminListTableState>({
  pager: {
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
  },
});

const paginatedData = computed<Row[]>(() => {
  return paginateItems(props.displayData, state.pager);
});

const onPageClick = (pageItem: PageItem) => {
  state.pager.currentPage = pageItem.pageNum;
};

// 表示フィールドは field 定義由来の動的アクセスになるため、文字列化して返す
const fieldValue = (row: Row, name: keyof Row & string): string => {
  const value = row[name];
  if (value == null) return '';

  return String(value);
};

const edit = (obj: Row) => {
  emit('start-edit', obj);
};

const destroy = (obj: Row) => {
  emit('start-destroy', obj);
};

const visibleCustomButtons = (obj: Row) => {
  return props.customButtons.filter(customButton => customButton.isVisible(obj));
};

const shouldShowEditButton = (row: Row) => {
  return props.alwaysShowButtons || props.canEdit(row);
};

const shouldShowDeleteButton = (row: Row) => {
  return props.alwaysShowButtons || props.canDelete(row);
};
</script>

<style lang="scss" scoped>
.data-row {
  height: 48px;
}
.icon {
  height: 48px;
  max-width: 52px;
}
</style>
